import React, { useCallback, useMemo, useRef, useState } from 'react'
import { HexShape } from './HexShape'
import {
  CELL_H,
  CELL_W,
  EASE_BOUNCE,
  HEX_ASPECT,
  HOVER_SCALE,
  INFO_CAP_WIDTH,
  LAYER_OPACITY,
  LAYER_WIDTHS,
  hexHeightForWidth,
  tierTone,
  topCapFill,
} from './hexTheme'
import { layersFromTick, type MetricKey, type MetricLayer } from './layers'

interface Props {
  id: string
  label: string
  tick: unknown
  selected?: boolean
  /** 0–1 opacity for collapsed fade-out during expand. */
  fade?: number
  onToggleExpand: () => void
}

function widthForRank(worstFirstIdx: number, layerCount: number): number {
  // worst (0) → smallest width; best (last) → largest — radii stay fixed on hover
  const inv = (layerCount - 1) - worstFirstIdx
  const idx = Math.max(0, Math.min(LAYER_WIDTHS.length - 1, inv))
  return LAYER_WIDTHS[idx]!
}

/** Pointy-top hex radius (center → vertex) from flat-to-flat width. */
function radiusForWidth(width: number): number {
  return hexHeightForWidth(width) / 2
}

function hexPoints(cx: number, cy: number, r: number): Array<[number, number]> {
  return Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 180) * (60 * i - 30)
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as [number, number]
  })
}

function pointInPoly(x: number, y: number, pts: Array<[number, number]>): boolean {
  let inside = false
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i]![0], yi = pts[i]![1]
    const xj = pts[j]![0], yj = pts[j]![1]
    const intersect = ((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)
    if (intersect) inside = !inside
  }
  return inside
}

function clientCount(tick: unknown): number | null {
  const n = (tick as { usersTotal?: unknown } | null | undefined)?.usersTotal
  if (typeof n === 'number' && Number.isFinite(n)) return Math.max(0, Math.round(n))
  return null
}

export function HexLayerStack({ id, label, tick, selected, fade = 1, onToggleExpand }: Props) {
  const layers = useMemo(() => layersFromTick(tick), [tick])
  const clients = clientCount(tick)
  const hasMetricData = useMemo(
    () => layers.some((l) => l.tier != null),
    [layers],
  )
  const topFill = useMemo(() => topCapFill(layers.map((l) => l.tier)), [layers])
  const rootRef = useRef<HTMLDivElement>(null)
  const [hoverKey, setHoverKey] = useState<MetricKey | null>(null)
  const [tileHover, setTileHover] = useState(false)

  const withR = useMemo(() => {
    const ordered = layers.slice(0, 5)
    return ordered.map((l, idx) => {
      const w = widthForRank(idx, ordered.length)
      return { l, idx, w, r: radiusForWidth(w) }
    })
  }, [layers])

  // Hovered metric comes to front (draw last). Radii never change.
  const front = hoverKey
    ? withR.find((x) => x.l.key === hoverKey) ?? null
    : null

  const handleMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = rootRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    // Map through CSS scale so ring hit-test stays in unscaled local coords.
    const mx = ((e.clientX - rect.left) / rect.width) * CELL_W
    const my = ((e.clientY - rect.top) / rect.height) * CELL_H
    const cx = CELL_W / 2
    const cy = CELL_H / 2
    const d = Math.hypot(mx - cx, my - cy)

    // Same as old canvas: among hexes containing the pointer, prefer the
    // layer whose radius boundary is closest to the pointer (ring hit).
    let best: MetricKey | null = null
    let bestScore = Infinity
    for (const { l, r } of withR) {
      const pts = hexPoints(cx, cy, r)
      if (!pointInPoly(mx, my, pts)) continue
      const score = Math.abs(r - d)
      if (score < bestScore) {
        bestScore = score
        best = l.key
      }
    }
    setHoverKey(best)
  }, [withR])

  const handleLeave = useCallback(() => {
    setHoverKey(null)
    setTileHover(false)
  }, [])

  const handleEnter = useCallback(() => {
    setTileHover(true)
  }, [])

  const nonFront = withR
    .filter((x) => !front || x.l.key !== front.l.key)
    .sort((a, b) => b.w - a.w)

  const showMetric = Boolean(front)
  const isHoveringLayer = hoverKey != null

  return (
    <div
      ref={rootRef}
      onMouseEnter={handleEnter}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      onClick={(e) => {
        e.stopPropagation()
        onToggleExpand()
      }}
      style={{
        position: 'relative',
        width: CELL_W,
        height: CELL_H,
        margin: '0 auto',
        cursor: 'pointer',
        opacity: fade,
        transform: tileHover ? `scale(${HOVER_SCALE})` : 'scale(1)',
        transition: `transform 280ms ${EASE_BOUNCE}, opacity 200ms ease`,
        outline: selected ? '1.5px solid rgba(77,157,224,0.7)' : 'none',
        outlineOffset: 4,
        transformOrigin: 'center center',
        zIndex: tileHover ? 2 : 1,
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onToggleExpand()
        }
      }}
      aria-label={
        hasMetricData && clients != null
          ? `${label}, ${clients} clients`
          : hasMetricData
            ? label
            : `${label}, no telemetry`
      }
    >
      {/* Fixed-size layers: largest → smallest, hovered drawn last on top */}
      {nonFront.map(({ l, w }) => (
        <LayerVisual
          key={l.key}
          tileId={id}
          layer={l}
          width={w}
          highlighted={false}
          z={1}
        />
      ))}

      {front && (
        <LayerVisual
          key={`front-${front.l.key}`}
          tileId={id}
          layer={front.l}
          width={front.w}
          highlighted
          z={6}
        />
      )}

      {/* Info cap — solid status slate when metrics exist; radial grey when no data */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: INFO_CAP_WIDTH,
          height: INFO_CAP_WIDTH * HEX_ASPECT,
          zIndex: showMetric ? 4 : 7,
          pointerEvents: 'none',
        }}
      >
        <HexShape
          tone="info"
          width={INFO_CAP_WIDTH}
          gradKey={`${id}-info`}
          opacity={1}
          solid={hasMetricData}
          fillColor={hasMetricData ? topFill : undefined}
        />
      </div>

      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 8,
          pointerEvents: 'none',
          textAlign: 'center',
          padding: '0 8px',
        }}
      >
        {showMetric && front ? (
          <>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(238,240,248,0.88)', lineHeight: 1.2 }}>
              {front.l.label}
            </div>
            <div style={{ fontSize: 12, color: 'rgba(238,240,248,0.78)', marginTop: 2, fontFamily: 'var(--font-mono, monospace)' }}>
              {front.l.valueText ?? '—'}
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                fontSize: 8,
                fontWeight: 700,
                color: 'rgba(238,240,248,0.95)',
                lineHeight: 1.15,
                maxWidth: INFO_CAP_WIDTH - 8,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={label}
            >
              {label}
            </div>
            {hasMetricData && clients != null ? (
              <div style={{ fontSize: 10, color: 'rgba(238,240,248,0.72)', marginTop: 3, fontWeight: 500 }}>
                {clients}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

/** Visual-only layer — no mouse handlers; sizes never change on hover. */
function LayerVisual({
  tileId,
  layer,
  width,
  highlighted,
  z,
}: {
  tileId: string
  layer: MetricLayer
  width: number
  /** Hovered front layer — full opacity; siblings stay reduced. */
  highlighted: boolean
  z: number
}) {
  const height = hexHeightForWidth(width)
  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        width,
        height,
        marginLeft: -width / 2,
        marginTop: -height / 2,
        zIndex: z,
        pointerEvents: 'none',
      }}
    >
      <HexShape
        tone={tierTone(layer.tier)}
        width={width}
        gradKey={`${tileId}-${layer.key}-${width}`}
        opacity={highlighted ? 1 : LAYER_OPACITY}
      />
    </div>
  )
}
